import React, { createContext, useContext, useMemo } from 'react';
import { createFeatureFlagClient } from '../services/featureFlagClient';
const FeatureFlagContext = createContext(null);
/**
 * Provider for feature flag functionality.
 * Wrap your app with this provider to enable feature flag hooks.
 *
 * @param baseUrl - The API base URL (e.g., 'http://localhost:3005/api/v1')
 * @param authToken - The JWT auth token for API requests
 */
export function FeatureFlagProvider({ baseUrl, authToken, children }) {
    // Create client instance - memoize based on baseUrl
    const client = useMemo(() => createFeatureFlagClient(baseUrl), [baseUrl]);
    // Set auth token synchronously during render so it's available immediately
    // when child effects fire (child useEffects run before parent useEffects,
    // so a useEffect here would leave client.authToken stale when children fetch)
    client.setAuthToken(authToken);
    // Include authToken in context so hooks can react to changes
    const contextValue = useMemo(() => ({ client, authToken }), [client, authToken]);
    return (React.createElement(FeatureFlagContext.Provider, { value: contextValue }, children));
}
/**
 * Hook to access the feature flag client.
 * Must be used within a FeatureFlagProvider.
 */
export function useFeatureFlagClient() {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlagClient must be used within a FeatureFlagProvider');
    }
    return context.client;
}
/**
 * Hook to access the current auth token from the provider.
 * Used internally by feature flag hooks for reactivity.
 */
export function useFeatureFlagAuth() {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlagAuth must be used within a FeatureFlagProvider');
    }
    return context.authToken;
}
//# sourceMappingURL=FeatureFlagContext.js.map